import React, { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Chip,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import * as XLSX from 'xlsx';
import ResponsiveDialog from '../common/ResponsiveDialog';
import pettyCashApi from '../../services/pettyCashApi';
import type { ClaimLineInput, PettyCashConfig, VendorMatch } from '../../types/pettyCash';
import { IMPORT_COLUMNS, ImportRow, formatAmount, formatDate, parseImportTable, todayIso, validateLine } from '../../utils/pettyCash';

/**
 * Receipts from an Excel sheet: download the template (made here in the browser), fill one row per
 * receipt, upload it. Every row is checked, VAT suppliers are found in SAP by VAT number (or vendor code),
 * and only rows without problems are added. Receipt photos are attached afterwards, line by line.
 */

export function downloadReceiptTemplate(config: PettyCashConfig): void {
  const columns = config.company.vatRegistered ? IMPORT_COLUMNS : IMPORT_COLUMNS.filter((c) => !['type', 'vat', 'vatInvoice', 'amount', 'vatAmount'].includes(c.key));
  const sheet = XLSX.utils.aoa_to_sheet([columns.map((c) => c.header)]);
  sheet['!cols'] = columns.map((c) => ({ wch: Math.max(14, c.header.length + 4) }));
  const help = XLSX.utils.aoa_to_sheet([
    ['Column', 'What to enter'],
    ...columns.map((c) => [c.header, c.note]),
    [],
    ['Notes'],
    ['One row per receipt; keep the column names in the first row of the "Receipts" sheet. Empty rows are skipped.'],
    ['Rows with problems are shown and left out; fix them in the file and import again.'],
    ['Attach each receipt\'s photo afterwards, from the receipt\'s line on the claim.'],
  ]);
  help['!cols'] = [{ wch: 24 }, { wch: 110 }];
  const cats = XLSX.utils.aoa_to_sheet([['Expense categories'], ...config.categories.filter((c) => c.isActive).map((c) => [c.name])]);
  cats['!cols'] = [{ wch: 50 }];
  const book = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(book, sheet, 'Receipts');
  XLSX.utils.book_append_sheet(book, help, 'Instructions');
  XLSX.utils.book_append_sheet(book, cats, 'Categories');
  XLSX.writeFile(book, 'Petty_Cash_Receipts_Template.xlsx');
}

interface Props {
  open: boolean;
  companyId: number;
  config: PettyCashConfig;
  firstLineNum: number;
  onClose: () => void;
  onImport: (lines: ClaimLineInput[]) => void;
}

const LineImportDialog: React.FC<Props> = ({ open, companyId, config, firstLineNum, onClose, onImport }) => {
  const [fileName, setFileName] = useState('');
  const [rows, setRows] = useState<ImportRow[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setFileName(''); setRows(null); setBusy(false); setError(null);
  }, [open]);

  /** The SAP vendor of each VAT row: by VAT number, or by vendor code when the cell is not a VAT number. */
  const resolveVendors = async (parsed: ImportRow[]): Promise<ImportRow[]> => {
    const byVat = new Map<string, VendorMatch[]>();
    const byCode = new Map<string, VendorMatch | null>();
    for (const row of parsed) {
      if (row.line.lineType !== 'VAT' || !row.vendorBy || row.errors.length) continue;
      let vendor: VendorMatch | null = null;
      if (row.vendorBy === 'vat') {
        const digits = row.line.supplierVat || '';
        if (!byVat.has(digits)) byVat.set(digits, (await pettyCashApi.lookupVendorByVat(companyId, digits)).vendors);
        const found = byVat.get(digits) || [];
        vendor = found.find((v) => v.active) || found[0] || null;
        if (!vendor) row.errors.push(`No vendor in SAP has VAT number ${digits}: define the supplier first`);
      } else {
        const key = row.vendorKey.toUpperCase();
        if (!byCode.has(key)) {
          const found = await pettyCashApi.searchVendors(companyId, row.vendorKey);
          byCode.set(key, found.find((v) => v.cardCode.toUpperCase() === key) || null);
        }
        vendor = byCode.get(key) || null;
        if (!vendor) row.errors.push(`No SAP vendor has the code or VAT number "${row.vendorKey}"`);
      }
      if (!vendor) continue;
      if (!vendor.active) { row.errors.push(`Vendor ${vendor.cardCode} is inactive in SAP`); continue; }
      // The server refuses to submit it (checkLine 'vendor-vat').
      if (!vendor.vatValid) { row.errors.push(`SAP holds an invalid VAT number for ${vendor.cardCode} ${vendor.cardName}: correct it in SAP first, or import the receipt as Non-VAT`); continue; }
      row.line.cardCode = vendor.cardCode;
      row.line.supplierName = vendor.cardName;
      row.line.supplierVat = row.line.supplierVat || vendor.vat.replace(/\D/g, '');
    }
    // The same checks as a receipt typed on screen, except the photo (attached afterwards).
    const ctx = {
      settings: { ...config.settings, requireImage: false },
      vatRegistered: config.company.vatRegistered,
      vatCountry: config.company.vatCountry,
      categories: config.categories,
      vatCodes: config.vatCodes,
      today: todayIso(),
      hasImage: true,
    };
    for (const row of parsed) {
      if (row.errors.length) continue;
      for (const c of validateLine(row.line, ctx)) if (c.level === 'error') row.errors.push(c.message);
    }
    return parsed;
  };

  const onFile = async (file: File | undefined) => {
    if (!file) return;
    setFileName(file.name);
    setError(null);
    setRows(null);
    setBusy(true);
    try {
      const book = XLSX.read(await file.arrayBuffer(), { type: 'array', cellDates: false });
      const first = book.Sheets[book.SheetNames.includes('Receipts') ? 'Receipts' : book.SheetNames[0]];
      const table = XLSX.utils.sheet_to_json<unknown[]>(first, { header: 1, raw: true, defval: '' });
      const parsed = parseImportTable(table, {
        settings: config.settings,
        vatRegistered: config.company.vatRegistered,
        vatCountry: config.company.vatCountry,
        categories: config.categories,
        vatCodes: config.vatCodes,
        firstLineNum,
      });
      if (parsed.missingHeader) { setError('No receipts found. Keep the column names in the first row (see the template).'); return; }
      if (!parsed.rows.length) { setError('The sheet has no receipt rows.'); return; }
      setRows(await resolveVendors(parsed.rows));
    } catch (e: any) {
      setError(e?.response?.data?.message || 'The file could not be read. Use the template (.xlsx).');
    } finally {
      setBusy(false);
    }
  };

  const ready = (rows || []).filter((r) => !r.errors.length);
  const problems = (rows || []).filter((r) => r.errors.length);

  return (
    <ResponsiveDialog open={open} onClose={busy ? undefined : onClose} maxWidth="lg" fullWidth aria-labelledby="pc-import-title">
      <DialogTitle id="pc-import-title">Import receipts from Excel</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
            <Button variant="outlined" startIcon={<DownloadIcon />} onClick={() => downloadReceiptTemplate(config)}>Download template</Button>
            <Button component="label" startIcon={<UploadFileIcon />} disabled={busy}>
              Choose the filled file (.xlsx)
              <input hidden type="file" accept=".xlsx,.xls,.csv" onChange={(e) => { void onFile(e.target.files?.[0]); e.target.value = ''; }} />
            </Button>
            {fileName ? <Typography variant="body2">{fileName}</Typography> : null}
            {busy ? <CircularProgress size={18} /> : null}
          </Stack>
          {error ? <Alert severity="error">{error}</Alert> : null}
          {rows ? (
            <>
              <Stack direction="row" spacing={1}>
                <Chip size="small" color="success" label={`${ready.length} ready`} />
                {problems.length ? <Chip size="small" color="error" label={`${problems.length} with problems (left out)`} /> : null}
              </Stack>
              <TableContainer sx={{ maxHeight: 420 }}>
                <Table size="small" stickyHeader aria-label="Receipts read from the file">
                  <TableHead>
                    <TableRow>
                      <TableCell>Row</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Supplier</TableCell>
                      <TableCell>Invoice no.</TableCell>
                      <TableCell>Date</TableCell>
                      <TableCell align="right">Total</TableCell>
                      <TableCell>Category</TableCell>
                      <TableCell>Problems</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.map((r) => (
                      <TableRow key={r.rowNumber}>
                        <TableCell>{r.rowNumber}</TableCell>
                        <TableCell>{r.line.lineType === 'VAT' ? 'VAT' : 'Non-VAT'}</TableCell>
                        <TableCell>{r.line.cardCode ? `${r.line.cardCode} ` : ''}{r.line.supplierName || r.vendorKey}</TableCell>
                        <TableCell>{r.line.invoiceNo}</TableCell>
                        <TableCell>{formatDate(r.line.invoiceDate)}</TableCell>
                        <TableCell align="right">{formatAmount(r.line.total)}</TableCell>
                        <TableCell>{r.categoryText}</TableCell>
                        <TableCell>
                          {r.errors.length ? r.errors.map((e, i) => <Typography key={`${i}-${e}`} variant="caption" color="error" display="block">{e}</Typography>) : <Typography variant="caption" sx={{ color: '#1e6b2a' }}>Ready</Typography>}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={onClose} disabled={busy}>Cancel</Button>
        <Button onClick={() => onImport(ready.map((r) => r.line))} disabled={busy || !ready.length}>
          {ready.length ? `Add ${ready.length} receipt${ready.length === 1 ? '' : 's'}` : 'Add receipts'}
        </Button>
      </DialogActions>
    </ResponsiveDialog>
  );
};

export default LineImportDialog;
